import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { formatBytes } from "@src/cloud-connect/utils/format-bytes";
import { RootState } from "@src/reducers/store";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { startInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/start-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { updateShowOtaAcceptAndPushFirmwareModal } from "@src/reducers/your-reducer";
import {
  OTA_OBJECT_TYPE,
  type InstallAndActionBlock,
} from "@src/cloud-connect/ota-update/constants";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

export const useOtaAcceptAndPushFirmwareModal = (
  data: ActiveInstallationProcessPayload,
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >,
) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const source = useSelector(
    (state: RootState) => state.yourSlice?.connectionView?.source,
  );
  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const isBackupInstallation =
    data?.firmware?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;
  const [isAcceptAndPushLoading, setIsAcceptAndPushLoading] = useState(false);

  const texts = {
    closeModalAriaLabel: isBackupInstallation
      ? i18n("modules.otaAcceptAndPushFirmwareModal.closeModalAriaLabelBackup")
      : i18n(
          "modules.otaAcceptAndPushFirmwareModal.closeModalAriaLabelFirmware",
        ),
    title: isBackupInstallation
      ? i18n("modules.otaAcceptAndPushFirmwareModal.titleBackup")
      : i18n("modules.otaAcceptAndPushFirmwareModal.titleFirmware"),
    firmwareSectionTitle: isBackupInstallation
      ? i18n("modules.otaAcceptAndPushFirmwareModal.backupSectionTitle")
      : i18n("modules.otaAcceptAndPushFirmwareModal.firmwareSectionTitle"),
    uploadedAtLabel: i18n(
      "modules.otaAcceptAndPushFirmwareModal.uploadedAtLabel",
    ),
    machineSectionTitle: i18n(
      "modules.otaAcceptAndPushFirmwareModal.machineSectionTitle",
    ),
    machineIdLabel: i18n(
      "modules.otaAcceptAndPushFirmwareModal.machineIdLabel",
    ),
    machineIpLabel: i18n(
      "modules.otaAcceptAndPushFirmwareModal.machineIpLabel",
    ),
    foldersSectionTitle: isBackupInstallation
      ? i18n("modules.otaAcceptAndPushFirmwareModal.backupFoldersSectionTitle")
      : i18n(
          "modules.otaAcceptAndPushFirmwareModal.firmwareFoldersSectionTitle",
        ),
    firmwarePackageFoldersSectionTitle: isBackupInstallation
      ? i18n(
          "modules.otaAcceptAndPushFirmwareModal.backupPackageFoldersSectionTitle",
        )
      : i18n(
          "modules.otaAcceptAndPushFirmwareModal.firmwarePackageFoldersSectionTitle",
        ),
    warningText: i18n("modules.otaAcceptAndPushFirmwareModal.warningText"),
    cancelButtonText: i18n(
      "modules.otaAcceptAndPushFirmwareModal.cancelButtonText",
    ),
    acceptAndPushButtonText: isBackupInstallation
      ? i18n(
          "modules.otaAcceptAndPushFirmwareModal.acceptAndPushButtonTextBackup",
        )
      : i18n(
          "modules.otaAcceptAndPushFirmwareModal.acceptAndPushButtonTextFirmware",
        ),
    startSuccessMessage: isBackupInstallation
      ? i18n("modules.otaAcceptAndPushFirmwareModal.startSuccessMessageBackup")
      : i18n(
          "modules.otaAcceptAndPushFirmwareModal.startSuccessMessageFirmware",
        ),
    startFailureMessage: isBackupInstallation
      ? i18n("modules.otaAcceptAndPushFirmwareModal.startFailureMessageBackup")
      : i18n(
          "modules.otaAcceptAndPushFirmwareModal.startFailureMessageFirmware",
        ),
    startErrorMessage: isBackupInstallation
      ? i18n("modules.otaAcceptAndPushFirmwareModal.startErrorMessageBackup")
      : i18n("modules.otaAcceptAndPushFirmwareModal.startErrorMessageFirmware"),
  };

  const firmware = {
    fileName: data?.firmware?.filename,
    fileSize: formatBytes(data?.firmware?.size_bytes ?? 0),
    uploadedAt: data?.firmware?.uploaded_at,
  };

  const machine = {
    name: source?.name,
    id: source?.source_id,
    ip: source?.dsn,
  };

  const targetPath = data?.target_path || "";

  const handleCloseModal = () => {
    dispatch(updateShowOtaAcceptAndPushFirmwareModal(false));
  };

  const handleAcceptAndPushFirmware = async () => {
    if (!data?.update_id) {
      errorSnackbar(texts.startErrorMessage);
      return;
    }

    setIsAcceptAndPushLoading(true);

    try {
      const response = await startInstallationProcessUseCase(data.update_id);

      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from startInstallationProcessUseCase:",
          response,
        );

      if (response.success) {
        successSnackbar(texts.startSuccessMessage, 5000);
        setInstallAndActionBlock(null);
        handleCloseModal();
        return;
      }

      errorSnackbar(response.error || texts.startFailureMessage, 5000);
      setIsAcceptAndPushLoading(false);
    } catch (error) {
      console.error(error);
      errorSnackbar(texts.startErrorMessage, 5000);
      setIsAcceptAndPushLoading(false);
    }
  };

  return {
    firmware,
    machine,
    folders: data?.archive_structure_root_level ?? [],
    target_path_folders:
      data?.update_details?.folder_structure_result?.entries?.map(
        (e) => e?.path,
      ) ?? [],
    texts,
    targetPath,
    handleCloseModal,
    handleAcceptAndPushFirmware,
    isAcceptAndPushLoading,
  };
};
